import { validateCarrierPostalCode } from "@components/commerce/baseForms/utils/validateCarrierPostalCode";
import { Market, type CountryCode } from "@config/site/types";
import { getQueryClient } from "@lib/helpers/queryClient";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { postalCodeRegex } from "@lib/validators/regex/postalCodeRegex";

// Markets with a carrier-capability validator wired up. An explicit list keeps adding a market
// a deliberate change. Shared by both checkout (v1) and checkoutV2, and by every delivery
// method. Home Delivery and Express alike, since either can be served by a carrier that
// cannot reach a given postcode.
const CARRIER_CHECKED_COUNTRIES: readonly CountryCode[] = ["SE"];

export const isCarrierCheckedCountry = (country: CountryCode): boolean =>
    CARRIER_CHECKED_COUNTRIES.includes(country);

type Args = {
    country: CountryCode;
    carrierCode: string | null | undefined;
    postalCode: string;
};

export const checkCarrierServesPostcode = async ({
    country,
    carrierCode,
    postalCode,
}: Args): Promise<boolean> => {
    if (!isCarrierCheckedCountry(country)) return true;
    if (!postalCode?.trim()) return true;

    if (!carrierCode?.trim()) {
        Logger.warn(
            ServiceType.WEB,
            "Carrier postcode check skipped: method has no carrierCode in a checked market",
            { tag: LogTag.SHIPPING, country, postalCode }
        );
        return true;
    }

    const trimmedPostalCode = postalCode.trim();
    if (!postalCodeRegex[Market.SE]?.test(trimmedPostalCode)) return false;

    const result = await validateCarrierPostalCode({
        carrierCode: carrierCode.trim(),
        postalCode: trimmedPostalCode.replace(/\s/g, ""),
        queryClient: getQueryClient(),
    });

    return Boolean(result?.isValid);
};
